import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';

const ArRequestDecision = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [userData, setUserData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelled, setCancelled] = useState(false);
  const [error, setError] = useState('');
  const [roleError, setRoleError] = useState('');
  const [roles, setRoles] = useState([]);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);

  const currentUserId = localStorage.getItem('id');

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    if (currentUserId) {
      axiosInstance.get(`/user/${currentUserId}/`)
        .then((response) => {
          setUserData(response.data);
          return axiosInstance.get(`/get-ar-request/${id}/`);
        })
        .then((response) => {
          setAttendanceData(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
          setLoading(false);
          setError('Failed to load request data');
        });
    }
  }, [currentUserId, id]);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRoles([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, [currentUserId]);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: roles[1].id });
      navigate('/user-dashboard');
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const handleApproveRequest = () => {
    axiosInstance.post(`/attendance/regularization/${id}/decision/`, { status: 'approved' })
      .then((response) => {
        console.log('Attendance request approved successfully');
        setAttendanceData({ ...attendanceData, status: 'approved' });
      })
      .catch((error) => {
        console.error('Error approving attendance request:', error);
        setError('Failed to approve request');
      });
  };

  const handleRejectRequest = () => {
    axiosInstance.post(`/attendance/regularization/${id}/decision/`, { status: 'rejected' })
      .then((response) => {
        console.log('Attendance request rejected successfully');
        setAttendanceData({ ...attendanceData, status: 'rejected' });
      })
      .catch((error) => {
        console.error('Error rejecting attendance request:', error);
        setError('Failed to reject request');
      });
  };

  const handleCancelRequest = () => {
    axiosInstance.post(`/cancel-ar-request/${id}/`)
      .then((response) => {
        console.log('Attendance request cancelled successfully');
        setCancelled(true);
        setAttendanceData(null);
      })
      .catch((error) => {
        console.error('Error cancelling attendance request:', error);
        setError('Failed to cancel request');
      });
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'status-badge approved';
      case 'rejected':
        return 'status-badge rejected';
      case 'pending':
        return 'status-badge pending';
      case 'cancelled':
        return 'status-badge cancelled';
      default:
        return 'status-badge';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
        <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
        <Sidebar 
          isSidebarVisible={isSidebarVisible} 
          currentUser={userData} 
          activeRole={userData?.role?.name}
          error={roleError}
          handleSwitchRole={handleSwitchRole}
          handleLogout={handleLogout}
        />
        <main className="dashboard-main-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading request details...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={userData} 
        activeRole={userData?.role?.name}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout}
      />
      
      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>📋 AR Request Decision</h1>
          <p>Review and process attendance regularization request</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="form-error-message" style={{ marginBottom: '16px' }}>
            <span>⚠️</span> {error}
          </div>
        )}

        {/* Success Message */}
        {cancelled && (
          <div className="form-success-message" style={{ marginBottom: '16px' }}>
            <span>✓</span> Request Cancelled Successfully!
          </div>
        )}

        {/* Main Card */}
        {attendanceData ? (
          <div className="dash-card">
            {/* Card Header with Status */}
            <div className="ar-decision-card-header">
              <div className="ar-header-left">
                <div className="ar-header-icon">
                  {attendanceData.status === 'approved' ? '✓' : 
                   attendanceData.status === 'rejected' ? '✕' : '⏳'}
                </div>
                <div>
                  <h3>Request Details</h3>
                  <p>Applied on {formatDateTime(attendanceData.regularization_date)}</p>
                </div>
              </div>
              <span className={getStatusBadgeClass(attendanceData.status)}>
                {attendanceData.status || 'Pending'}
              </span>
            </div>

            <div className="dash-card-divider"></div>

            {/* Employee Info */}
            <div className="ar-section">
              <div className="ar-section-title">
                <span>👤</span> Employee Information
              </div>
              <div className="ar-info-grid">
                <div className="ar-info-item">
                  <span className="ar-label">Employee Name</span>
                  <span className="ar-value">{attendanceData.user_name || '-'}</span>
                </div>
                <div className="ar-info-item">
                  <span className="ar-label">Employee Code</span>
                  <span className="ar-value">{attendanceData.employee_code || '-'}</span>
                </div>
              </div>
            </div>

            {/* Time Details */}
            <div className="ar-section">
              <div className="ar-section-title">
                <span>🕐</span> Attendance Time
              </div>
              <div className="ar-info-grid">
                <div className="ar-info-item">
                  <span className="ar-label">In Time</span>
                  <span className="ar-value in-value">
                    {attendanceData.in_date} {attendanceData.in_time || '-'}
                  </span>
                </div>
                <div className="ar-info-item">
                  <span className="ar-label">Out Time</span>
                  <span className="ar-value out-value">
                    {attendanceData.out_date} {attendanceData.out_time || '-'}
                  </span>
                </div>
                <div className="ar-info-item">
                  <span className="ar-label">Approved On</span>
                  <span className="ar-value">{formatDateTime(attendanceData.updated_at)}</span>
                </div>
              </div>
            </div>

            {/* Reason */}
            <div className="ar-section">
              <div className="ar-section-title">
                <span>📝</span> Reason for Regularization
              </div>
              <div className="ar-reason-text">
                {attendanceData.reason || 'No reason provided'}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="ar-decision-actions">
              {attendanceData.status === 'pending' || !attendanceData.status ? (
                <>
                  <button className="ar-btn approve" onClick={handleApproveRequest}>
                    ✓ Approve
                  </button>
                  <button className="ar-btn reject" onClick={handleRejectRequest}>
                    ✕ Reject
                  </button>
                </>
              ) : (
                <div className="ar-status-text">
                  This request has been {attendanceData.status}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="dash-card">
            <div className="ar-empty-state">
              <div className="empty-icon">📋</div>
              <h3>No Request Found</h3>
              <p>No attendance regularization request found.</p>
              <button className="dash-btn" onClick={() => navigate('/all-requests')}>
                View All Requests
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Inline Styles */}
      <style>{`
        .ar-decision-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px;
          background: linear-gradient(135deg, #f47522 0%, #e06a1c 100%);
          border-radius: 8px;
          color: white;
          margin-bottom: 12px;
        }

        .ar-header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .ar-header-icon {
          width: 36px;
          height: 36px;
          background: rgba(255,255,255,0.2);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
        }

        .ar-header-left h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
        }

        .ar-header-left p {
          margin: 4px 0 0;
          font-size: 13px;
          opacity: 0.9;
        }

        .ar-header-left .status-badge {
          background: rgba(255,255,255,0.25);
          color: white;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
        }

        .ar-section {
          margin-bottom: 20px;
        }

        .ar-section-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 12px;
        }

        .ar-info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }

        .ar-info-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
        }

        .ar-label {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .ar-value {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
        }

        .ar-value.in-value {
          color: #22c55e;
        }

        .ar-value.out-value {
          color: #ef4444;
        }

        .ar-reason-text {
          padding: 14px;
          background: #f8fafc;
          border-radius: 8px;
          border-left: 3px solid #f47522;
          font-size: 14px;
          color: #475569;
          line-height: 1.5;
        }

        .ar-decision-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .ar-btn {
          flex: 1;
          padding: 12px 20px;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .ar-btn.approve {
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: white;
        }

        .ar-btn.approve:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(34,197,94,0.3);
        }

        .ar-btn.reject {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }

        .ar-btn.reject:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(239,68,68,0.3);
        }

        .ar-status-text {
          flex: 1;
          text-align: center;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
        }

        .ar-empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          text-align: center;
        }

        .ar-empty-state .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .ar-empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 8px;
        }

        .ar-empty-state p {
          font-size: 14px;
          color: #64748b;
          margin: 0 0 20px;
        }

        @media (max-width: 768px) {
          .ar-decision-card-header {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .ar-header-left {
            flex-direction: column;
          }

          .ar-info-grid {
            grid-template-columns: 1fr;
          }

          .ar-decision-actions {
            flex-direction: column;
          }

          .ar-btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ArRequestDecision;

